import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import IconButton from '@material-ui/core/IconButton';

const MIN_ZOOM = 0.25;
const MAX_ZOOM = 4;
const STEP = 0.25;

// Visualizador de imagem com zoom (+/-), indicador de % e arraste para navegar
// (pan), no estilo Google Maps. Recebe apenas a URL (blob/remota) da imagem.
function ImageZoomViewer({ src, alt, height }) {
  const [zoom, setZoom] = useState(1);
  const [natural, setNatural] = useState(null);
  const [fitScale, setFitScale] = useState(1);
  const [dragging, setDragging] = useState(false);

  const wrapperRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    setZoom(1);
    setNatural(null);
    setFitScale(1);
  }, [src]);

  const changeZoom = useCallback((delta) => {
    setZoom((z) =>
      Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round((z + delta) * 100) / 100))
    );
  }, []);

  // 100% = imagem inteira encaixada na área; o zoom multiplica a partir daí.
  function handleImageLoad(event) {
    const img = event.target;
    const wrapper = wrapperRef.current;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    setNatural({ w: natW, h: natH });

    if (wrapper && natW && natH) {
      const fit = Math.min(
        wrapper.clientWidth / natW,
        wrapper.clientHeight / natH,
        1
      );
      setFitScale(fit > 0 ? fit : 1);
    }
  }

  function handleMouseDown(event) {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const pannable =
      wrapper.scrollWidth > wrapper.clientWidth ||
      wrapper.scrollHeight > wrapper.clientHeight;
    if (!pannable) return;

    event.preventDefault();
    dragRef.current = {
      x: event.clientX,
      y: event.clientY,
      left: wrapper.scrollLeft,
      top: wrapper.scrollTop,
    };
    setDragging(true);
  }

  function handleMouseMove(event) {
    const wrapper = wrapperRef.current;
    const start = dragRef.current;
    if (!wrapper || !start) return;
    event.preventDefault();
    wrapper.scrollLeft = start.left - (event.clientX - start.x);
    wrapper.scrollTop = start.top - (event.clientY - start.y);
  }

  function stopDrag() {
    dragRef.current = null;
    setDragging(false);
  }

  const percent = Math.round(zoom * 100);
  const displayW = natural ? natural.w * fitScale * zoom : null;
  const displayH = natural ? natural.h * fitScale * zoom : null;

  return (
    <div style={{ position: 'relative', width: '100%', height }}>
      <div
        ref={wrapperRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={stopDrag}
        onMouseLeave={stopDrag}
        style={{
          width: '100%',
          height: '100%',
          overflow: 'auto',
          background: '#f4f6f8',
          textAlign: 'center',
          cursor: dragging ? 'grabbing' : 'grab',
        }}
      >
        <div
          style={{
            margin: '0 auto',
            width: displayW ? `${displayW}px` : 'auto',
            height: displayH ? `${displayH}px` : 'auto',
            transition: 'width 0.12s ease, height 0.12s ease',
          }}
        >
          <img
            src={src}
            alt={alt}
            onLoad={handleImageLoad}
            draggable={false}
            style={{
              width: displayW ? '100%' : 'auto',
              height: displayH ? '100%' : 'auto',
              maxWidth: natural ? 'none' : '100%',
              maxHeight: natural ? 'none' : height,
              display: 'block',
              pointerEvents: 'none',
            }}
          />
        </div>
      </div>

      <div
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'absolute',
          bottom: 16,
          right: 16,
          display: 'flex',
          alignItems: 'center',
          background: 'rgba(60,64,67,0.92)',
          borderRadius: 24,
          padding: '2px 4px',
          color: '#fff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
          zIndex: 2,
        }}
      >
        <IconButton
          size="small"
          style={{ color: '#fff' }}
          onClick={() => changeZoom(-STEP)}
          disabled={zoom <= MIN_ZOOM}
          aria-label="Diminuir zoom"
        >
          <RemoveIcon fontSize="small" />
        </IconButton>
        <span
          onClick={() => setZoom(1)}
          style={{
            minWidth: 48,
            textAlign: 'center',
            fontSize: 13,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {percent}%
        </span>
        <IconButton
          size="small"
          style={{ color: '#fff' }}
          onClick={() => changeZoom(STEP)}
          disabled={zoom >= MAX_ZOOM}
          aria-label="Aumentar zoom"
        >
          <AddIcon fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
}

ImageZoomViewer.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  height: PropTypes.string,
};

ImageZoomViewer.defaultProps = {
  src: null,
  alt: 'anexo',
  height: '72vh',
};

export default ImageZoomViewer;
